import Button from "@mui/material/Button"
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress"
import Paper from "@mui/material/Paper"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { useState } from "react"
import { useSelector } from "react-redux"

import Row from "../../../common/components/Row"
import { INITIAL_FREE_CANDIDATES } from "../../users/billing"
import { selectSubscription } from "../../users/reducer"
import SelectPlanDrawer from "../components/SelectPlanDrawer"

export default function CandidateUsageProgressCard() {
  const [planDetailsDrawerOpen, setPlanDetailsDrawerOpen] = useState(false)
  const subscription = useSelector(selectSubscription)

  const percentageCandidatesUsed = Math.round(
    (100 * subscription.usedCandidateCount) / INITIAL_FREE_CANDIDATES
  )

  function handleDrawerClose() {
    setPlanDetailsDrawerOpen(false)
  }

  return (
    <PaperContainer elevation={0}>
      <Row
        sx={{
          justifyContent: "space-between",
          gap: 2,
          mb: -0.5,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: (theme) => theme.palette.text.primary,
            fontSize: "0.875rem",
          }}
        >
          Free candidate leads
        </Typography>
        <UnlimitedAccessButton
          variant="contained"
          color="primary"
          onClick={() => setPlanDetailsDrawerOpen(true)}
        >
          Get unlimited access
        </UnlimitedAccessButton>
      </Row>
      <Row
        sx={{
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontWeight: 700,
            color: (theme) => theme.palette.text.primary,
            fontSize: "1rem",
            lineHeight: 1.5,
          }}
        >
          {subscription.usedCandidateCount} of {INITIAL_FREE_CANDIDATES} (
          {percentageCandidatesUsed}% used)
        </Typography>
      </Row>
      <UsageLinearProgress
        variant="determinate"
        value={percentageCandidatesUsed}
      />
      <SelectPlanDrawer
        open={planDetailsDrawerOpen}
        onClose={handleDrawerClose}
      />
    </PaperContainer>
  )
}

const PaperContainer = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.spacing(2.5),
  border: `1px solid ${theme.palette.colors.brand200}`,
  background: `linear-gradient(135deg, ${theme.palette.colors.brand50}, ${theme.palette.colors.gray50})`,
  boxShadow: `0 2px 8px 0 ${theme.palette.colors.brand200}40, 0 1px 2px 0 ${theme.palette.colors.gray200}`,
  padding: theme.spacing(2),
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.only("xs")]: {
    padding: theme.spacing(1.5),
  },
}))

const UnlimitedAccessButton = styled(Button)(({ theme }) => ({
  fontWeight: 600,
  textTransform: "none",
  borderRadius: theme.spacing(1.5),
  padding: theme.spacing(1.25, 3.5),
  fontSize: "0.9375rem",
  maxWidth: "240px",
  width: "auto",
  minWidth: "200px",
  background: `linear-gradient(135deg, ${theme.palette.colors.brand500}, ${theme.palette.colors.brand700})`,
  boxShadow: `0 4px 16px ${theme.palette.colors.brand400}50, inset 0 1px 0 rgba(255, 255, 255, 0.2)`,
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  "&:hover": {
    background: `linear-gradient(135deg, ${theme.palette.colors.brand600}, ${theme.palette.colors.brand700})`,
    boxShadow: `0 6px 20px ${theme.palette.colors.brand400}60, inset 0 1px 0 rgba(255, 255, 255, 0.3)`,
    transform: "translateY(-1px)",
  },
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
}))

const UsageLinearProgress = styled(LinearProgress)(({ theme }) => ({
  borderRadius: "3px",
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: "3px",
    background: `linear-gradient(90deg,
    ${theme.palette.colors.purple},
    ${theme.palette.colors.pink})`,
  },
}))
